/**
 * Proposal Detail Page
 *
 * View proposal details and accept/reject.
 * Issues #47, #51 - Term Sheet Viewing and Acceptance
 * Issue #475 - Shows linked application when available
 */

import ApplicationIcon from '@mui/icons-material/Assignment';
import AcceptIcon from '@mui/icons-material/Check';
import RejectIcon from '@mui/icons-material/Close';
import ProposalIcon from '@mui/icons-material/Description';
import DrawIcon from '@mui/icons-material/Draw';
import {
  AstorsReadCard,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  Grid,
  iconSizes,
  MagicInput,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { ProposalAction } from '@ops-matrix/shared-types';
import DOMPurify from 'dompurify';
import { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DocumentSigningModal } from '../../components/esign';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import {
  useConsumerProposal,
  useConsumerTermSheet,
  useEsignSubmissions,
  useRespondToProposal,
  useRespondToTermSheet,
} from '../../lib/trpc/hooks';
import { formatTermSheetTitle } from './ProposalsPage';

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  sent: 'primary',
  viewed: 'primary',
  accepted: 'success',
  rejected: 'error',
  expired: 'warning',
  signing_requested: 'primary',
  signed: 'success',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'New',
  viewed: 'Viewed',
  accepted: 'Accepted',
  rejected: 'Rejected',
  expired: 'Expired',
  signing_requested: 'Signing Requested',
  signed: 'Signed',
};

export default function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const [confirmDialog, setConfirmDialog] = useState<ProposalAction | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Issue #1083: Detect if viewing a term sheet
  const isTermSheet = searchParams.get('type') === 'term_sheet';

  // Fetch proposal OR term sheet details
  const {
    data: proposalData,
    isLoading: proposalLoading,
    isError: proposalError,
    error: proposalErr,
  } = useConsumerProposal(isTermSheet ? null : (id ?? null));
  const {
    data: termSheetData,
    isLoading: termSheetLoading,
    isError: termSheetError,
    error: termSheetErr,
  } = useConsumerTermSheet(isTermSheet ? (id ?? null) : null);

  const isLoading = isTermSheet ? termSheetLoading : proposalLoading;
  const isError = isTermSheet ? termSheetError : proposalError;
  const error = isTermSheet ? termSheetErr : proposalErr;

  // Normalize term sheet data to proposal shape for display
  const proposal =
    isTermSheet && termSheetData
      ? {
          ...termSheetData,
          title: formatTermSheetTitle(
            termSheetData.borrower,
            termSheetData.termSheetNumber,
            t('consumer.proposals.termSheetLabel'),
          ),
          content: null as string | null, // Term sheets show structured data, not HTML content
        }
      : proposalData;

  // Respond mutations
  const respondProposalMutation = useRespondToProposal();
  const respondTermSheetMutation = useRespondToTermSheet();
  const respondMutation = isTermSheet ? respondTermSheetMutation : respondProposalMutation;

  const handleAccept = () => {
    setConfirmDialog(ProposalAction.ACCEPT);
  };

  const handleReject = () => {
    setConfirmDialog(ProposalAction.REJECT);
  };

  const handleConfirmResponse = async () => {
    if (!id || !confirmDialog) return;

    try {
      if (isTermSheet) {
        // Issue #1083: Use term sheet respond mutation
        await respondTermSheetMutation.mutateAsync({
          termSheetId: id,
          action: confirmDialog === ProposalAction.ACCEPT ? 'accept' : 'reject',
          reason: confirmDialog === ProposalAction.REJECT ? rejectReason : undefined,
        });
      } else {
        await respondProposalMutation.mutateAsync({
          id,
          action: confirmDialog,
          reason: confirmDialog === ProposalAction.REJECT ? rejectReason : undefined,
        });
      }
      setConfirmDialog(null);
      setRejectReason('');
    } catch {
      // Mutation error is surfaced inline via the `respondMutation.isError`
      // GlassAlert above. Suppressing here keeps the dialog interactive.
    }
  };

  const canRespond = proposal && (proposal.status === 'sent' || proposal.status === 'viewed');
  const canSign = proposal?.status === 'signing_requested';

  // E-sign: signing state and submitter resolution
  const { consumer } = useConsumerAuth();
  const [signingModalOpen, setSigningModalOpen] = useState(false);
  const [selectedSubmitterId, setSelectedSubmitterId] = useState<number | null>(null);
  const { data: esignData, refetch: refetchEsignSubmissions } = useEsignSubmissions(
    canSign && consumer?.id ? { consumerId: consumer.id } : undefined,
  );

  const findSubmitterId = useCallback((): number | null => {
    if (!esignData?.submissions || !proposal) return null;
    const tsNumber = (termSheetData as { termSheetNumber?: string } | null | undefined)?.termSheetNumber ?? '';
    for (const sub of esignData.submissions) {
      const name = sub.name || '';
      if ((tsNumber && name.includes(tsNumber)) || sub.case_id === proposal.caseId) {
        const submitter = sub.submitters?.find(
          (s: { status?: string; id: number }) =>
            s.status === 'pending' || s.status === 'sent' || s.status === 'opened',
        );
        if (submitter) return submitter.id;
      }
    }
    return null;
  }, [esignData, proposal, termSheetData]);

  if (isLoading) {
    return (
      <PageTemplate variant="detail" icon={ProposalIcon}>
        <GlassCard>
          <ShimmerLoader variant="rectangular" height={400} />
        </GlassCard>
      </PageTemplate>
    );
  }

  if (isError || !proposal) {
    return (
      <PageTemplate variant="detail" icon={ProposalIcon}>
        <GlassAlert severity="error" visible={true}>
          {error?.message || 'Proposal not found'}
        </GlassAlert>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate variant="detail" title={proposal.title} icon={ProposalIcon}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          mb: 3,
        }}
      >
        <GlassChip
          label={STATUS_LABELS[proposal.status] || proposal.status}
          color={STATUS_COLORS[proposal.status] || 'default'}
        />
        {canSign && (
          <GlassButton
            variant="glow"
            startIcon={<DrawIcon />}
            onClick={() => {
              const submitterId = findSubmitterId();
              if (submitterId) {
                setSelectedSubmitterId(submitterId);
                setSigningModalOpen(true);
              }
            }}
          >
            {t('esign.signNow')}
          </GlassButton>
        )}
        {canRespond && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <GlassButton
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={handleReject}
              disabled={respondMutation.isPending}
            >
              {t('common.reject')}
            </GlassButton>
            <GlassButton
              variant="glow"
              color="success"
              startIcon={<AcceptIcon />}
              onClick={handleAccept}
              disabled={respondMutation.isPending}
            >
              {t('common.accept')}
            </GlassButton>
          </Box>
        )}
      </Box>
      {respondMutation.isError && (
        <GlassAlert severity="error" visible={true} sx={{ mb: 2 }}>
          {t('consumer.proposals.failedToRespond')}
        </GlassAlert>
      )}
      {/* Astor's read — per-offer briefing card. Body is rendered verbatim
          from the server (NOT translated). TODO(backend-wiring): replace the
          static narrative below with `proposal.explain` once 29-case-mgmt
          emits it on the `OfferDto`. */}
      <Box sx={{ mb: 3 }}>
        <AstorsReadCard
          variant="read"
          narrative={
            (proposal as Record<string, unknown>).explain
              ? String((proposal as Record<string, unknown>).explain)
              : t('consumer.offers.astorExplain.title')
          }
        />
      </Box>
      {/* Proposal Info */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <GlassCard neonAccent="cyan">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ProposalIcon color="primary" />
              <Typography variant="h6">{t('consumer.proposals.proposalContent')}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {isTermSheet && termSheetData ? (
              /* Issue #1083: Term sheet structured content */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {termSheetData.borrower && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {t('consumer.proposals.borrowerInfo')}
                    </Typography>
                    <Typography>{termSheetData.borrower.principal_name}</Typography>
                    {termSheetData.borrower.email && (
                      <Typography variant="body2" color="textSecondary">
                        {termSheetData.borrower.email}
                      </Typography>
                    )}
                  </Box>
                )}
                {termSheetData.property && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {t('consumer.proposals.propertyInfo')}
                    </Typography>
                    <Typography>{termSheetData.property.address}</Typography>
                  </Box>
                )}
                {termSheetData.calculatedTerms && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {t('consumer.proposals.loanTerms')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('consumer.proposals.loanAmount')}
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          ${Number(termSheetData.calculatedTerms.loan_amount || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('consumer.proposals.interestRate')}
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>{termSheetData.calculatedTerms.interest_rate}%</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('consumer.proposals.term')}
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {termSheetData.calculatedTerms.term_months} {t('termsheets.months')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t('consumer.proposals.monthlyPayment')}
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          ${Number(termSheetData.calculatedTerms.monthly_payment || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                {termSheetData.documentUrl && (
                  <GlassButton
                    variant="outlined"
                    onClick={() => window.open(termSheetData.documentUrl, '_blank', 'noopener,noreferrer')}
                  >
                    {t('consumer.proposals.downloadPdf')}
                  </GlassButton>
                )}
              </Box>
            ) : proposal.content ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  minHeight: 300,
                  '& p': { mb: 2 },
                  '& h1, & h2, & h3': { mb: 1, mt: 2 },
                }}
                // SECURITY: Sanitize HTML content to prevent XSS attacks
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(proposal.content) }}
              />
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">{t('consumer.proposals.noContent')}</Typography>
              </Box>
            )}
          </GlassCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <GlassCard neonAccent="green">
            <Typography variant="h6" gutterBottom>
              {t('common.details')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('common.status')}
                </Typography>
                <GlassChip
                  label={STATUS_LABELS[proposal.status] || proposal.status}
                  color={STATUS_COLORS[proposal.status] || 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>

              {proposal.sentAt && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('consumer.proposals.received')}
                  </Typography>
                  <Typography>
                    {new Date(proposal.sentAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              )}

              {proposal.viewedAt && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('consumer.proposals.firstViewed')}
                  </Typography>
                  <Typography>
                    {new Date(proposal.viewedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              )}

              {proposal.expiresAt && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('consumer.proposals.expires')}
                  </Typography>
                  <Typography color={new Date(proposal.expiresAt) < new Date() ? 'error.main' : 'text.primary'}>
                    {new Date(proposal.expiresAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              )}

              {/* Issue #475: Show linked application */}
              {proposal.sourceCaseId && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t('proposal.relatedApplication')}
                  </Typography>
                  <GlassButton
                    variant="outlined"
                    size="small"
                    startIcon={<ApplicationIcon />}
                    onClick={() => navigate(`/applications/${proposal.sourceCaseId}`)}
                    sx={{ mt: 0.5 }}
                  >
                    {t('proposal.viewApplication')}
                  </GlassButton>
                </Box>
              )}
            </Box>
          </GlassCard>

          {/* Response Status Card */}
          {(proposal.status === 'accepted' || proposal.status === 'rejected') && (
            <GlassCard neonAccent={proposal.status === 'accepted' ? 'green' : 'cyan'} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('consumer.proposals.yourResponse')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ textAlign: 'center', py: 2 }}>
                {proposal.status === 'accepted' ? (
                  <>
                    <AcceptIcon sx={{ fontSize: iconSizes['3xl'], color: 'success.main', mb: 1 }} />
                    <Typography sx={{ color: 'success.main', fontWeight: 600 }}>
                      {t('consumer.proposals.youAccepted')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <RejectIcon sx={{ fontSize: iconSizes['3xl'], color: 'error.main', mb: 1 }} />
                    <Typography sx={{ color: 'error.main', fontWeight: 600 }}>
                      {t('consumer.proposals.youRejected')}
                    </Typography>
                  </>
                )}
              </Box>
            </GlassCard>
          )}
        </Grid>
      </Grid>
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog !== null} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>
          {confirmDialog === ProposalAction.ACCEPT
            ? t('consumer.proposals.acceptProposal')
            : t('consumer.proposals.rejectProposal')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog === ProposalAction.ACCEPT
              ? t('consumer.proposals.confirmAccept')
              : t('consumer.proposals.confirmReject')}
          </DialogContentText>
          {confirmDialog === ProposalAction.REJECT && (
            <MagicInput
              autoFocus
              margin="dense"
              label={t('consumer.proposals.reasonOptional')}
              multiline
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setConfirmDialog(null)} disabled={respondMutation.isPending}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            onClick={handleConfirmResponse}
            color={confirmDialog === ProposalAction.ACCEPT ? 'success' : 'error'}
            variant="glow"
            disabled={respondMutation.isPending}
          >
            {respondMutation.isPending
              ? t('common.submitting')
              : confirmDialog === ProposalAction.ACCEPT
                ? t('common.accept')
                : t('common.reject')}
          </GlassButton>
        </DialogActions>
      </Dialog>
      {/* E-Sign: Inline signing modal for signing_requested term sheets */}
      <DocumentSigningModal
        open={signingModalOpen}
        onClose={() => {
          setSigningModalOpen(false);
          setSelectedSubmitterId(null);
        }}
        submitterId={selectedSubmitterId}
        onSigningComplete={() => {
          refetchEsignSubmissions();
          setSigningModalOpen(false);
          setSelectedSubmitterId(null);
        }}
      />
    </PageTemplate>
  );
}
